// 3D로 장소 만들기: 장소 종류 → 규모 → 무대 자리 → 더할 것 → 카메라 시점 저장
// 닫기(✕·Esc·'닫기')는 창만 닫고 지금 화면에 머문다. '다 했어요'만 다음 단계(무대 디자인)로 간다.
import { CAPACITIES, capacityKo, EXTRAS, STAGE_PRESETS, VENUE_TYPES } from "../../shared/knowledge";
import type { Project } from "../../shared/types";
import { icon } from "../icons";
import { api, busy, chipGroup, clear, emptyState, h, helpTip, mediaUrl, pushLayer, segmented, toast, trapTab, venueGroups } from "../lib";
import { stageIcon } from "../stage-icons";
import type { Space3dView, SpaceConfig } from "../space3d";

const LENSES = [
  { id: "70", ko: "넓게" },
  { id: "50", ko: "보통" },
  { id: "32", ko: "좁게" },
];

let seq = 0;

export async function openSpaceBuilder(p0: Project, onChange: (p: Project) => void, onDone: (goNext: boolean) => void) {
  let p = p0;
  const b = p.data.brief;
  const sportsFirst = b.eventKind === "sports" || /체육|개회|폐회|경기/.test(b.eventType || "");
  const cfg: SpaceConfig = {
    venueType: p.data.model3d?.venueType || b.venueType || (sportsFirst ? "stadium" : "outdoor"),
    capacity: p.data.model3d?.capacity || b.capacity || VENUE_TYPES.find((v) => v.id === b.venueType)?.capacity || 10000,
    stage: p.data.model3d?.stage || (sportsFirst ? "spot-center" : "spot-end"),
    extras: p.data.model3d?.extras ? [...p.data.model3d.extras] : sportsFirst ? ["cauldron"] : [],
  };
  if (!VENUE_TYPES.some((v) => v.id === cfg.venueType)) cfg.venueType = "outdoor";

  const titleId = `builder-${++seq}`;
  const viewport = h("div", { class: "viewport" }, h("div", { class: "viewport-msg" }, h("span", { class: "spinner" }), "3D 화면을 준비하는 중"));
  const camSlot = h("div", { class: "vp-cams" });
  const lensSlot = h("div", { class: "vp-lens" });
  const left = h("div", { class: "builder-left" });
  const saved = h("div", { class: "saved-views", "data-gallery": "views" });
  const savedCount = h("span", { class: "count num" });
  const footStatus = h("div", { class: "builder-status" });
  const saveBtn = h("button", { class: "btn save-view", type: "button" }, icon("camera"), "이 시점 저장");
  const doneBtn = h("button", { class: "btn primary", type: "button" }, h("span", { class: "hide-sm" }, "다 했어요 · "), "무대 디자인으로", icon("chevron-right", 16));
  let view: Space3dView | null = null;
  let lens = "50";
  let customCam = false;
  let openSec: string | null = p.data.model3d ? null : "venue";

  const box = h(
    "div",
    { class: "builder", role: "dialog", "aria-modal": "true", "aria-labelledby": titleId, tabindex: "-1" },
    h(
      "div",
      { class: "builder-head" },
      h(
        "div",
        { class: "builder-title" },
        h("h2", { id: titleId }, "3D로 장소 만들기"),
        helpTip("장소와 규모를 고르면 3D 모형이 생겨요. 끌어서 돌리고 휠로 당겨 원하는 시점을 찾은 뒤 저장하세요. 저장한 시점이 그림의 카메라가 돼요. 여러 방향을 저장할수록 AI가 장소를 잘 이해해요."),
        h("p", { class: "sub hide-sm" }, "끌어서 돌리고 휠로 당겨 시점을 찾으세요"),
      ),
      h("button", { class: "btn ghost icon-only", type: "button", "aria-label": "닫기", onclick: () => close(false) }, icon("x", 20)),
    ),
    h(
      "div",
      { class: "builder-body" },
      left,
      h(
        "div",
        { class: "builder-right" },
        h(
          "div",
          { class: "viewport-wrap" },
          viewport,
          h("span", { class: "vp-legend" }, "흰색 구조물 · 남색 LED · 주황 사람 1.75m"),
          h("div", { class: "viewport-toolbar" }, camSlot, lensSlot),
        ),
        h("div", { class: "saved-head" }, saveBtn, h("span", { class: "small muted" }, "저장한 시점 ", savedCount)),
        saved,
      ),
    ),
    h("div", { class: "builder-foot" }, footStatus, h("button", { class: "btn ghost hide-sm", type: "button", onclick: () => close(false) }, "닫기"), doneBtn),
  );
  const shell = h("div", { class: "builder-wrap" }, box);
  box.addEventListener("keydown", (e) => trapTab(e, box));
  let closed = false;
  const popLayer = pushLayer(() => close(false));
  document.body.appendChild(shell);
  box.focus({ preventScroll: true });

  function close(goNext: boolean) {
    if (closed) return;
    closed = true;
    popLayer();
    view?.dispose();
    shell.remove();
    onDone(goNext);
  }

  // ── 왼쪽: 접는 칸 네 개 ──
  function venueCards() {
    const card = (v: (typeof VENUE_TYPES)[number]) =>
      h(
        "button",
        {
          type: "button",
          class: `option-card${cfg.venueType === v.id ? " on" : ""}`,
          "aria-pressed": cfg.venueType === v.id ? "true" : "false",
          "data-k": `venue-${v.id}`,
          onclick: () => {
            cfg.venueType = v.id;
            if (!CAPACITIES.includes(cfg.capacity) || cfg.capacity > v.capacity * 2.5 || cfg.capacity < v.capacity / 5) cfg.capacity = v.capacity;
            if (v.group === "general" && cfg.stage === "ring-swirl") cfg.stage = "spot-end";
            changed();
          },
        },
        h("span", { class: "t" }, v.ko),
        v.hint ? h("span", { class: "d" }, v.hint) : null,
      );
    // 행사에 맞는 묶음이 먼저, '기타'는 두 묶음 뒤 맨 끝
    const { groups, other } = venueGroups(sportsFirst);
    return [
      ...groups.map((g) => h("div", { class: "venue-group" }, h("h4", { class: "group-title" }, g.ko), h("div", { class: "option-grid one" }, g.list.map(card)))),
      h("div", { class: "venue-group" }, h("div", { class: "option-grid one" }, other.map(card))),
    ];
  }

  function stageCards(list: typeof STAGE_PRESETS) {
    return h(
      "div",
      { class: "stage-grid two" },
      list.map((s) => {
        const pic = h("div", { class: "stage-icon" });
        pic.innerHTML = stageIcon(s.id);
        return h(
          "button",
          {
            type: "button",
            class: `stage-card${cfg.stage === s.id ? " on" : ""}`,
            "aria-pressed": cfg.stage === s.id ? "true" : "false",
            title: s.hint || null,
            "data-k": `stage-${s.id}`,
            onclick: () => {
              cfg.stage = s.id;
              changed();
            },
          },
          pic,
          h("b", null, s.ko.replace(/^무대 자리:\s*/, "")),
        );
      }),
    );
  }

  function drawLeft() {
    const focusKey = (document.activeElement as HTMLElement | null)?.dataset?.k;
    clear(left);
    const venue = VENUE_TYPES.find((v) => v.id === cfg.venueType);
    const stage = STAGE_PRESETS.find((s) => s.id === cfg.stage);
    const sections = [
      { id: "venue", n: 1, ko: "장소", value: venue?.ko || "", body: () => venueCards() },
      {
        id: "cap",
        n: 2,
        ko: "규모",
        value: capacityKo(cfg.capacity),
        body: () =>
          chipGroup(
            CAPACITIES.map((n) => ({ id: String(n), ko: capacityKo(n) })),
            [String(cfg.capacity)],
            false,
            (v) => {
              cfg.capacity = Number(v[0]);
              changed();
            },
            "규모",
          ),
      },
      {
        id: "stage",
        n: 3,
        ko: "무대 자리",
        value: (stage?.ko || "").replace(/^무대 자리:\s*/, ""),
        body: () => [
          h("p", { class: "small muted" }, "무대 모양은 다음 단계에서 AI가 그려요"),
          stageCards(STAGE_PRESETS.filter((s) => s.spot)),
          h("details", { class: "more-shapes", open: !STAGE_PRESETS.find((s) => s.id === cfg.stage)?.spot }, h("summary", { class: "small" }, "모양도 미리 정하기 ", h("span", { class: "opt" }, "(선택)")), stageCards(STAGE_PRESETS.filter((s) => !s.spot))),
        ],
      },
      {
        id: "extras",
        n: 4,
        ko: "더할 것",
        value: cfg.extras.map((x) => EXTRAS.find((e) => e.id === x)?.ko).filter(Boolean).join(", ") || "없음",
        body: () =>
          chipGroup(
            EXTRAS.map((x) => ({ id: x.id, ko: x.ko })),
            cfg.extras,
            true,
            (v) => {
              cfg.extras = v;
              changed();
            },
            "더할 것",
          ),
      },
    ];
    for (const s of sections) {
      const open = openSec === s.id;
      const bodyId = `${titleId}-${s.id}`;
      left.append(
        h(
          "div",
          { class: `acc${open ? " open" : ""}` },
          h(
            "button",
            {
              class: "acc-head",
              type: "button",
              "aria-expanded": open ? "true" : "false",
              "aria-controls": bodyId,
              "data-k": `acc-${s.id}`,
              onclick: () => {
                openSec = open ? null : s.id;
                drawLeft();
              },
            },
            h("span", { class: "acc-num" }, String(s.n)),
            h("span", { class: "acc-title" }, s.ko),
            h("span", { class: "acc-value" }, s.value),
            icon("chevron-down", 18),
          ),
          open ? h("div", { class: "acc-body", id: bodyId }, s.body()) : null,
        ),
      );
    }
    if (focusKey) left.querySelector<HTMLElement>(`[data-k="${focusKey}"]`)?.focus({ preventScroll: true });
  }

  function drawCams() {
    clear(camSlot);
    clear(lensSlot);
    if (!view) return;
    camSlot.append(
      segmented(
        view.presets().map((c) => ({ id: c.id, ko: c.ko })),
        customCam ? null : view.currentPreset(),
        (id) => {
          if (!id) return;
          view!.setCamera(id);
          customCam = false;
        },
        "카메라",
      ),
    );
    lensSlot.append(
      segmented(
        LENSES,
        lens,
        (id) => {
          lens = id || "50";
          view!.setFov(Number(lens));
        },
        "렌즈",
      ),
    );
  }

  function drawSaved() {
    clear(saved);
    const views = p.data.space.filter((s) => s.source === "model3d");
    savedCount.textContent = String(views.length);
    if (!views.length) {
      saved.append(emptyState({ icon: "camera", title: "아직 저장한 시점이 없어요", body: "첫 시점이 컷의 카메라가 돼요", compact: true }));
    } else {
      for (const s of views)
        saved.append(
          h(
            "figure",
            { class: "view-item" },
            h("div", { class: "thumb wide" }, h("img", { class: "zoomable", src: mediaUrl(s.key), alt: s.label }), s.base ? h("span", { class: "thumb-badge" }, icon("star", 12), "기준") : null),
            h("figcaption", { class: "xs one-line" }, s.label),
          ),
        );
    }
    footStatus.classList.toggle("warn", !views.length);
    footStatus.replaceChildren(...(views.length ? [`저장한 시점 ${views.length}개 · 첫 번째가 기준 그림`] : [icon("alert", 16), "시점을 하나 이상 저장해 주세요"]));
  }

  function changed() {
    drawLeft();
    view?.update(cfg);
    drawCams();
  }

  saveBtn.addEventListener("click", () =>
    busy(saveBtn, "저장하는 중", async () => {
      if (!view) throw new Error("3D 공간이 아직 준비되지 않았어요.");
      // 1) 공간 설정을 정한다 (바뀌었으면 요소와 조건이 새로 만들어지고, 예전 시점 그림은 빠진다)
      const r = await api<{ project: Project; removedViews: number }>(`/api/projects/${p.id}/model3d`, { body: cfg });
      p = r.project;
      if (r.removedViews) toast(`장소를 바꿔서 예전 시점 그림 ${r.removedViews}장을 뺐어요.`, "info", 6000);
      // 2) 지금 시점을 그림으로
      const blob = await view.capture();
      const camKo = customCam ? "직접 정한 시점" : view.presets().find((c) => c.id === view!.currentPreset())?.ko || "시점";
      const n = p.data.space.filter((s) => s.source === "model3d").length + 1;
      const label = `${camKo}${n > 1 ? ` (${n})` : ""}`;
      p = await api<Project>(`/api/projects/${p.id}/space?label=${encodeURIComponent(label)}&w=1920&h=1080&source=model3d&camera=${encodeURIComponent(camKo)}`, { raw: blob, rawType: "image/jpeg" });
      onChange(p);
      drawSaved();
      toast(n === 1 ? "기준 그림으로 저장했어요. 다른 방향도 하나 더 저장해 보세요." : "저장했어요.", "ok");
    }),
  );

  doneBtn.addEventListener("click", () =>
    busy(doneBtn, "정리하는 중", async () => {
      const r = await api<{ project: Project; removedViews: number }>(`/api/projects/${p.id}/model3d`, { body: cfg });
      p = r.project;
      onChange(p);
      close(true);
    }),
  );

  drawLeft();
  drawSaved();
  try {
    const url = "/assets/space3d.js";
    const mod: typeof import("../space3d") = await import(/* @vite-ignore */ url);
    if (closed) return;
    clear(viewport);
    view = mod.mountSpace3d(viewport, cfg);
    view.onUserMove(() => {
      if (customCam) return;
      customCam = true;
      drawCams();
    });
    drawCams();
  } catch (err) {
    clear(viewport);
    viewport.append(h("div", { class: "viewport-msg" }, "3D 화면을 열지 못했어요. 크롬이나 엣지 최신 버전에서 다시 열어 주세요."));
    console.error(err);
  }
}
